import { flatten, splitEvery, transpose } from 'rambda/immutable';
import type { Dispatch, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Container, Flexbox, Icon, Typography } from '@seuk/design-system/atoms';
import { TextField } from '@seuk/design-system/molecules';
import { theme as defaultTheme } from '@seuk/design-system/theme';
import { sanitizeRegexInput } from '@seuk/util';

import type { SearchResult, SearchEventResultType } from '../../services/search';

export function interlaceArrays<T>({ chunkSize, arrays }: { chunkSize: number; arrays: T[][] }): readonly T[] {
  // create chunks for each array
  const chunks = arrays.map((arr) => splitEvery(chunkSize, arr));
  // transpose the chunks and flatten the result
  return flatten(transpose(chunks));
}
const SearchSection = styled.section`
  background: ${({ theme }) => theme.palette.background.base};
  padding: ${({ theme }) => theme.spacing(12)} 0;
`;

const SearchResultItemContainer = styled(Flexbox)`
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: center;
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(8)};
  color: ${({ theme }) => theme.palette.text.onSurface.strong};
  && > svg {
    color: ${({ theme }) => theme.palette.text.onSurface.inactive};
  }

  &:hover,
  &:active,
  &:focus {
    background-color: ${({ theme }) => theme.palette.background.base};
    && > svg {
      color: ${({ theme }) => theme.palette.focus.strong};
    }
  }
`;

function makeBold(title: string, highlight: string): ReactNode {
  const sanitizeRegex = new RegExp(`(${sanitizeRegexInput(highlight)})`, 'gi');
  const splitTitle = title.split(sanitizeRegex).filter(Boolean);

  return (
    <Typography>
      {splitTitle.map((part) => (sanitizeRegex.test(part) ? <b key={part}>{part}</b> : <span key={part}>{part}</span>))}
    </Typography>
  );
}

const SearchResultItem = ({
  title,
  highlight,
  onSelect,
  compact,
}: {
  title: string;
  highlight?: string;
  onSelect: () => void;
  compact?: boolean;
}) => {
  return (
    <SearchResultItemContainer
      role="option"
      onClick={() => onSelect()}
      onKeyDownCapture={({ key }) => key === 'Enter' && onSelect()}
      tabIndex={0}
    >
      {!compact && <Icon name="ArrowRight" color="currentColor" />}
      <Typography>{highlight ? makeBold(title, highlight) : title}</Typography>
    </SearchResultItemContainer>
  );
};

const IconContainer = styled(Flexbox)`
  align-items: center;
  padding: 0 0 0 ${({ theme }) => theme.spacing(3)};
`;

const SearchResultCategoryTitle = styled(Typography)`
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(4)} ${({ theme }) => theme.spacing(1)};
`;

const SearchResultCategoryHead = styled(Flexbox)`
  padding: 0 ${({ theme }) => theme.spacing(6)} 0 0;
`;

type SearchResultWithOrigin = SearchResult & { origin: SearchEventResultType };

const SearchResultCategory = ({
  title,
  items,
  onSelectItem,
  query,
  itemOptions,
}: {
  title: string;
  items: SearchResultWithOrigin[];
  onSelectItem?: (item: SearchResultWithOrigin, index: number) => void;
  query?: string;
  itemOptions?: { compact?: boolean };
}) => {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <Flexbox flexDirection="column">
      <SearchResultCategoryHead flexDirection="row" justifyContent="space-between" alignItems="center">
        <SearchResultCategoryTitle variant="small" weight="medium" color={defaultTheme.palette.text.onSurface.subtle}>
          {title}
        </SearchResultCategoryTitle>
        <Typography variant="small" weight="normal" color={defaultTheme.palette.text.onSurface.inactive}>
          ({items.length} results)
        </Typography>
      </SearchResultCategoryHead>
      <Flexbox flexDirection="column" role="listitem" tabIndex={-1}>
        {items.map((result, index) => (
          <SearchResultItem
            key={`search-result-${result.url}`}
            title={result.title}
            highlight={query}
            compact={itemOptions?.compact}
            onSelect={() => onSelectItem?.(result, index)}
          />
        ))}
      </Flexbox>
    </Flexbox>
  );
};

const SpanNoResult = styled(Typography)`
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(4)};
  color: ${({ theme }) => theme.palette.text.onSurface.strong};
`;

const SearchResultsContainer = styled.div`
  // 26 rem == 10 entries to display in height
  max-height: ${({ theme }) => theme.spacing(26 * 4)};
  overflow-y: auto;
`;

interface SearchProps {
  initialValue: string;
  onQueryChange?: Dispatch<string>;
  request?: {
    query: string;
    results: SearchResult[];
    error?: Error | null;
  };
  onSelectItem?: (
    query: string,
    item: SearchResult,
    index: number,
    origin: SearchEventResultType,
    items: SearchResult[]
  ) => void;
}

export function Search({ initialValue, onQueryChange, request, onSelectItem }: SearchProps) {
  const [value, setValue] = useState(initialValue);
  const query = request?.query.toLowerCase();

  //TODO: find a better abstraction for these
  const quickLinkResults = useMemo<SearchResultWithOrigin[]>(
    () =>
      request?.results
        ?.filter((result) => result.title.toLowerCase().includes(query))
        .map((item) => ({ ...item, origin: 'match' })) ?? [],
    [query, request?.results]
  );
  const relatedArticlesResults = useMemo<SearchResultWithOrigin[]>(
    () =>
      request?.results
        ?.filter((result) => !result.title.toLowerCase().includes(query))
        .map((item) => ({ ...item, origin: 'topics' })) ?? [],
    [query, request?.results]
  );

  const filteredResults = useMemo(
    // MAX 30 elements to display, by chunks of 5, starting with exact matches
    () => interlaceArrays({ chunkSize: 5, arrays: [quickLinkResults, relatedArticlesResults] }).slice(0, 30),
    [quickLinkResults, relatedArticlesResults]
  );

  useEffect(() => {
    onQueryChange?.(value);
  }, [value, onQueryChange]);

  const autocompleteItems = useMemo(() => {
    if (!request || request?.error) {
      return null;
    }
    if (request.results.length > 0) {
      return (
        <SearchResultsContainer>
          <SearchResultCategory
            title="Is your question here?"
            items={filteredResults}
            query={query}
            onSelectItem={(item, index) => onSelectItem?.(query, item, index, item.origin, filteredResults)}
          />
        </SearchResultsContainer>
      );
    }
    return <SpanNoResult>Sorry, we can&apos;t find anything.</SpanNoResult>;
  }, [filteredResults, onSelectItem, query, request]);

  return (
    <SearchSection>
      <Container>
        <Flexbox flexDirection="column" gap="14px">
          <Typography variant="h3" color={defaultTheme.palette.text.onSurface.strong}>
            How we can help you?
          </Typography>
          <TextField
            id="query"
            startAdornment={
              <IconContainer as="label" htmlFor="query">
                <Icon name="Search" color={defaultTheme.palette.focus.strong} />
              </IconContainer>
            }
            name="search-query"
            type="search"
            autocompleteItems={autocompleteItems}
            error={
              request?.error
                ? {
                    message: 'Oops, something is wrong. Please refresh the page or search again.',
                    type: 'required',
                  }
                : undefined
            }
            autoComplete="off"
            value={value}
            onChange={(evt) => setValue(evt.target.value)}
            placeholder="Enter one or more keywords"
          />
        </Flexbox>
      </Container>
    </SearchSection>
  );
}
